import { useState, useEffect, useRef } from "react";

const CATEGORIES = [
  { key: "automotive", label: "Automotive", icon: "🚗" },
  { key: "collision", label: "Collision", icon: "🔧" },
  { key: "marine", label: "Marine & RV", icon: "⚓" },
  { key: "carwash", label: "Car Wash", icon: "🚿" },
];

const BRANDS = [
  { name: "Acura", color: "#1B1B1B", accent: "#E82C2A", category: "automotive" },
  { name: "BMW", color: "#1C69D4", accent: "#000000", category: "automotive" },
  { name: "GM Brands", subtitle: "Buick · Cadillac · GMC", color: "#1A1A1A", accent: "#D4AF37", category: "automotive" },
  { name: "Chevrolet", color: "#CF9A1C", accent: "#1A1A1A", category: "automotive" },
  { name: "CDJR", subtitle: "Chrysler · Dodge · Jeep · Ram", color: "#0C2340", accent: "#BA0C2F", category: "automotive" },
  { name: "Honda", color: "#CC0000", accent: "#FFFFFF", category: "automotive" },
  { name: "INFINITI", color: "#1A1A1A", accent: "#7B868C", category: "automotive" },
  { name: "Kia", color: "#05141F", accent: "#BB162B", category: "automotive" },
  { name: "Mazda", color: "#910A2A", accent: "#1A1A1A", category: "automotive" },
  { name: "Mercedes-Benz", color: "#1A1A1A", accent: "#A3A3A3", category: "automotive" },
  { name: "Porsche", color: "#B12B28", accent: "#D5A439", category: "automotive" },
  { name: "Toyota", color: "#EB0A1E", accent: "#1A1A1A", category: "automotive" },
  { name: "Collision", color: "#4A5568", accent: "#ED8936", category: "collision" },
  { name: "Marine & RV", color: "#2B6CB0", accent: "#38B2AC", category: "marine" },
  { name: "Car Wash", color: "#319795", accent: "#FFFFFF", category: "carwash" },
];

const COMMON_LINKS = [
  { label: "Google Admin", url: "https://admin.google.com", icon: "🔧" },
  { label: "PDK", url: "https://pdk.io/systems/e54eefdd-1de0-4bc3-a939-0ed22077cf84", icon: "🔑" },
  { label: "CDK", url: "https://launch.cdkunify.com", icon: "💻" },
  { label: "Cox Auto", url: "https://signin.coxautoinc.com", icon: "🚘" },
];

const DEALER_LINKS = [
  { label: "DealersLink", url: "https://www.dealerslink.com", icon: "🚗" },
  { label: "AuctionAccess", url: "https://www.auctionaccess.com", icon: "🏷️" },
  { label: "StoneEagle", url: "https://www.stoneeagle.com", icon: "📊" },
  { label: "vAuto", url: "https://www.vauto.com", icon: "📈" },
];

const SERVICE_BRANDS = ["Collision", "Marine & RV", "Car Wash"];

const BRAND_LINKS = {
  "GM Brands": [
    { label: "GM AutoPartners", url: "https://dealer.autopartners.net", icon: "💻" },
  ],
  "Chevrolet": [
    { label: "GM AutoPartners", url: "https://dealer.autopartners.net", icon: "💻" },
  ],
  "BMW": [
    { label: "BMW SGate", url: "http://sgate.bmwgroup.com", icon: "💻" },
  ],
  "CDJR": [
    { label: "DealerConnect", url: "https://dealerconnect.chrysler.com", icon: "💻" },
  ],
  "Mercedes-Benz": [
    { label: "GEMS", url: "https://gems.daimler.com/gems/admin/user/user_list.xhtml", icon: "💻" },
  ],
  "Mazda": [
    { label: "OneMazdaUSA", url: "https://www.onemazdausa.com", icon: "💻" },
  ],
  "Kia": [
    { label: "KDealer", url: "https://www.kdealer.com", icon: "💻" },
  ],
  "Toyota": [
    { label: "RouteOne", url: "https://www.routeone.net/Web/BIServlet?captiveId=ROUTEONE", icon: "💻" },
  ],
  "Marine & RV": [
    { label: "AppOne", url: "https://home.appone.net", icon: "💻" },
  ],
};

function getLinksForBrand(brandName) {
  const specific = BRAND_LINKS[brandName] || [];
  const extra = SERVICE_BRANDS.includes(brandName) ? [] : DEALER_LINKS;
  return [...COMMON_LINKS, ...specific, ...extra];
}

function BrandIcon({ name, color, accent, icon }) {
  if (icon) {
    return <span style={{ fontSize: 28 }}>{icon}</span>;
  }
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 8,
      background: `linear-gradient(135deg, ${color}, ${color}dd)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: accent || "#fff",
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 700, fontSize: 14, letterSpacing: 1,
      border: `2px solid ${accent}44`,
    }}>
      {name.substring(0, 2).toUpperCase()}
    </div>
  );
}

function LinkButton({ link, brandColor, brandAccent, delay }) {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 16px",
        borderRadius: 10,
        background: hovered ? `${brandColor}22` : "#1a1d23",
        border: `1px solid ${hovered ? brandColor + "55" : "#2a2d35"}`,
        color: hovered ? brandAccent || "#e8e9ec" : "#9ca3af",
        textDecoration: "none",
        fontSize: 14, fontWeight: 500,
        transition: "all 0.2s ease",
        cursor: "pointer",
        animation: `slideUp 0.35s ease ${delay}ms both`,
      }}
    >
      <span style={{ fontSize: 18 }}>{link.icon}</span>
      {link.label}
    </a>
  );
}

function BrandSection({ brand, isOpen, onToggle, index }) {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen]);

  const links = getLinksForBrand(brand.name);

  return (
    <div
      style={{
        background: isOpen ? "#13151b" : "#0f1117",
        borderRadius: 14,
        border: `1px solid ${isOpen ? brand.color + "44" : "#1e2028"}`,
        overflow: "hidden",
        transition: "all 0.3s ease",
        animation: `fadeIn 0.4s ease ${index * 40}ms both`,
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex", alignItems: "center",
          padding: "18px 24px",
          background: "none", border: "none", cursor: "pointer",
          gap: 16,
          transition: "all 0.2s ease",
        }}
      >
        <BrandIcon name={brand.name} color={brand.color} accent={brand.accent} icon={brand.icon} />

        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: 18, fontWeight: 600,
            color: isOpen ? "#fff" : "#c9cad0",
            letterSpacing: 1,
            transition: "color 0.2s ease",
          }}>
            {brand.name.toUpperCase()}
          </div>
          {brand.subtitle && (
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
              {brand.subtitle}
            </div>
          )}
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
            {links.length} link{links.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: isOpen ? brand.color + "22" : "#1a1d23",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.3s ease",
          border: `1px solid ${isOpen ? brand.color + "44" : "#2a2d35"}`,
        }}>
          <svg
            width="14" height="14" viewBox="0 0 14 14" fill="none"
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          >
            <path d="M3 5L7 9L11 5" stroke={isOpen ? brand.accent || "#fff" : "#6b7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>

      <div
        style={{
          maxHeight: isOpen ? contentHeight + 40 : 0,
          opacity: isOpen ? 1 : 0,
          overflow: "hidden",
          transition: "max-height 0.4s ease, opacity 0.3s ease",
        }}
      >
        <div ref={contentRef} style={{ padding: "0 24px 24px" }}>
          {isOpen && (
            <div style={{
              width: "100%", height: 1,
              background: `linear-gradient(90deg, transparent, ${brand.color}44, transparent)`,
              marginBottom: 20,
            }} />
          )}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 10,
          }}>
            {links.map((link, i) => (
              <LinkButton
                key={i}
                link={link}
                brandColor={brand.color}
                brandAccent={brand.accent}
                delay={i * 40}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DealershipDashboard() {
  const [openBrand, setOpenBrand] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const filtered = BRANDS.filter(b => {
    const matchesSearch = !search ||
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      (b.subtitle && b.subtitle.toLowerCase().includes(search.toLowerCase()));

    const matchesFilter = filter === "all" || b.category === filter;

    return matchesSearch && matchesFilter;
  });

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0c10",
      color: "#e8e9ec",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2a2d35; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #3a3d45; }

        input::placeholder { color: #4a4d55; }
      `}</style>

      {/* Header */}
      <div style={{
        borderBottom: "1px solid #1e2028",
        background: "linear-gradient(180deg, #0f1117 0%, #0a0c10 100%)",
      }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          padding: "32px 32px 28px",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            marginBottom: 6,
          }}>
            <div style={{
              width: 10, height: 10, borderRadius: "50%",
              background: "#22c55e",
              boxShadow: "0 0 8px #22c55e66",
            }} />
            <span style={{ fontSize: 11, color: "#6b7280", letterSpacing: 2, fontWeight: 600, textTransform: "uppercase" }}>
              IT Support Portal
            </span>
          </div>
          <h1 style={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: 36, fontWeight: 700,
            letterSpacing: 2,
            background: "linear-gradient(135deg, #fff 0%, #9ca3af 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            GOAC IT
          </h1>
          <p style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>
            Dealership Technology & Support Dashboard
          </p>
        </div>
      </div>

      {/* Controls */}
      <div style={{
        maxWidth: 1100, margin: "0 auto",
        padding: "24px 32px 0",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          flexWrap: "wrap",
        }}>
          {/* Search */}
          <div style={{
            flex: 1, minWidth: 260,
            position: "relative",
          }}>
            <svg
              width="16" height="16" viewBox="0 0 16 16" fill="none"
              style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}
            >
              <circle cx="7" cy="7" r="5.5" stroke="#4a4d55" strokeWidth="1.5" />
              <path d="M11 11L14 14" stroke="#4a4d55" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search brands..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px 12px 40px",
                borderRadius: 10,
                border: "1px solid #1e2028",
                background: "#12141a",
                color: "#e8e9ec",
                fontSize: 14,
                outline: "none",
                fontFamily: "'DM Sans', sans-serif",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => e.target.style.borderColor = "#3a3d55"}
              onBlur={(e) => e.target.style.borderColor = "#1e2028"}
            />
          </div>

          {/* Filter pills */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => { setFilter("all"); setOpenBrand(null); }}
              style={{
                padding: "10px 18px",
                borderRadius: 8,
                border: `1px solid ${filter === "all" ? "#3a3d55" : "#1e2028"}`,
                background: filter === "all" ? "#1a1d25" : "transparent",
                color: filter === "all" ? "#fff" : "#6b7280",
                fontSize: 13, fontWeight: 500,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                transition: "all 0.2s ease",
              }}
            >
              All
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat.key}
                onClick={() => {
                  setFilter(cat.key);
                  const brandsInCat = BRANDS.filter(b => b.category === cat.key);
                  if (brandsInCat.length === 1) {
                    setOpenBrand(brandsInCat[0].name);
                  } else {
                    setOpenBrand(null);
                  }
                }}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: `1px solid ${filter === cat.key ? "#3a3d55" : "#1e2028"}`,
                  background: filter === cat.key ? "#1a1d25" : "transparent",
                  color: filter === cat.key ? "#fff" : "#6b7280",
                  fontSize: 13, fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  transition: "all 0.2s ease",
                }}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Brand List by Category */}
      <div style={{
        maxWidth: 1100, margin: "0 auto",
        padding: "20px 32px 60px",
        display: "flex", flexDirection: "column",
        gap: 32,
      }}>
        {filtered.length === 0 && (
          <div style={{
            textAlign: "center", padding: 60,
            color: "#4a4d55", fontSize: 14,
          }}>
            No results found for "{search}"
          </div>
        )}

        {CATEGORIES.map(cat => {
          const brandsInCat = filtered.filter(b => b.category === cat.key);
          if (brandsInCat.length === 0) return null;

          return (
            <div key={cat.key}>
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                marginBottom: 12,
                padding: "0 4px",
              }}>
                <span style={{ fontSize: 18 }}>{cat.icon}</span>
                <h2 style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: 16, fontWeight: 600,
                  color: "#6b7280",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                }}>
                  {cat.label}
                </h2>
                <div style={{
                  flex: 1, height: 1,
                  background: "linear-gradient(90deg, #2a2d35, transparent)",
                }} />
              </div>
              <div style={{
                display: "flex", flexDirection: "column",
                gap: 8,
              }}>
                {brandsInCat.map((brand, i) => (
                  <BrandSection
                    key={brand.name}
                    brand={brand}
                    isOpen={openBrand === brand.name}
                    onToggle={() => setOpenBrand(openBrand === brand.name ? null : brand.name)}
                    index={i}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
